import type { AssignDeviceBox } from "./SvgOverlay/AssignDeviceBoxes";
import type { CableTraySegment } from "components/SmartSurveyor/SmartSurveyorV3/utils/fetchCableTakeoffPaths";
import { useAreaSelectGesture } from "./hooks/useAreaSelectGesture";
import type { AreaRectPlan } from "./hooks/useAreaSelectGesture";
import { AreaSelectBox } from "./AreaSelectBox";
import { PlanImage } from "./PlanImage";
import {
  type TouchEvent as ReactTouchEvent,
  type MouseEvent as ReactMouseEvent,
  type MouseEventHandler as ReactMouseEventHandler,
  type TouchEventHandler as ReactTouchEventHandler,
  type ReactNode,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import {
  OBJECT_BASE_SIZE,
  ZOOM_OPACITY_THRESHOLD,
  getCircleSize,
} from "components/features/ProgressReportViewer/utils";
import { useObjectNavigation } from "../ObjectNavigationBar/useObjectNavigation";
import { ObjectNavigationBar } from "../ObjectNavigationBar";
import type { NavigableObject } from "../ObjectNavigationBar/types";
import { ZoomControls } from "../ZoomControls";
import { PlanNameChip } from "../PlanNameChip";
import { SvgOverlay } from "./SvgOverlay";
import type { CablePaths } from "components/features/ProgressReportViewer/types";
import type { CircuitPathsState } from "../hooks/useCircuitCablePaths/types";
import { useFlashRipple } from "../hooks/useFlashRipple";
import type { RouteCreationOverlayState } from "./SvgOverlay/RoutesCreationLayer/types";
import type { PolygonsOverlayState } from "./SvgOverlay/PolygonsLayer/types";
import type { FloorPlanLayoutOverlay } from "./SvgOverlay/FloorPlanLayoutLayer";
import type {
  CircuitBoardMarker,
  RemovedDeviceMarker,
  UnroutedDeviceMarker,
} from "./SvgOverlay/CircuitPathsLayer/types";
import { useLatest } from "hooks/useLatest";
import { normalizeWheelDelta } from "utils/math/normalizeWheelDelta";
import {
  ZOOM_CONFIG,
  REFERENCE_IMAGE_SIZE,
  MIN_CIRCLE_SIZE_SCALE,
} from "./constants";
import { useKeyboardNavigation } from "../FloorPlanPanelPdf/useKeyboardNavigation";
import { useAnimatedZoom } from "./hooks/useAnimatedZoom";
import { findClickedRoute } from "./utils/findClickedRoute";
import { computeZoomToBbox } from "./utils/computeZoomToBbox";
import { getRasterRotationStyle } from "./utils/getRasterRotationStyle";
import { getRotatedSize } from "../FloorPlanPanelWrapper/createDisplayTransform";
import type { PageRotation } from "../FloorPlanPanelWrapper/createDisplayTransform";
import { isDetectedObject } from "./types";
import type { PlanExportAction } from "../PlanNameChip/types";
import type { CableReshapeView } from "components/SmartSurveyor/SmartSurveyorV3/CableTakeoffTabV3/routeReshape";
import type { BboxRect } from "../types";
import type {
  DetectedObject,
  ZoomToObject,
  ZoomToBbox,
  ZoomToElements,
  FloorPlanPanelHandlers,
  PickZoneCursor,
  ZoomState,
} from "./types";

interface IProgressReportViewer {
  plan: {
    previewUrl: string;
    name: string;
    imageWidthPx?: number;
    imageHeightPx?: number;
    isArchived?: boolean;
  };
  detectedObjects: DetectedObject[];
  selectedObject?: DetectedObject | null;
  setSelectedObject: (id: string) => void;
  isAutoZoomEnabled?: boolean;
  showObjectNavigation?: boolean;
  navigationObjects?: NavigableObject[];
  onAfterNavigate?: (object: NavigableObject) => void;
  zoomToFitOnImageLoad?: "always" | "initial" | "never";
  renderToolbar?: (props: {
    isDisabled: boolean;
    isHoveringPlan: boolean;
  }) => ReactNode;
  mode?: "default" | "pick_zone";
  exportActions?: PlanExportAction[];
  onMapClick?: (pos: { x: number; y: number }) => void;
  onMapMouseMove?: (pos: { x: number; y: number } | null) => void;
  /** Set to make a drag draw a selection rectangle (plan space); Space-drag or the middle button pans. */
  onAreaSelect?: (rect: AreaRectPlan) => void;
  // A default-mode click that hit no element and no route
  onEmptyMapClick?: (pos: { x: number; y: number }) => void;
  backgroundObjects?: DetectedObject[];
  floorPlanCablePaths?: CablePaths;
  circuitPaths?: CircuitPathsState | null;
  circuitStrokeScale?: number;
  circuitBoardMarkers?: CircuitBoardMarker[];
  cableGhostMarker?: { x: number; y: number; label: string } | null;
  removedMarkers?: RemovedDeviceMarker[];
  /** Devices that took no circuit at all — the fourth marker set on this plan. */
  unroutedDeviceMarkers?: UnroutedDeviceMarker[];
  /** Devices the armed pick could take — drawn neutral, never as a choice. */
  candidateDeviceMarkers?: { x: number; y: number }[];
  selectedDeviceMarkers?: { x: number; y: number }[];
  /** Pulse them: they were asked for, not merely caught by a box. */
  selectedDeviceMarkersBlink?: boolean;
  /** Draw the devices but not the cables between them. */
  cableTrays?: CableTraySegment[];
  highlightTrayType?: string | null;
  /** The tray type Karl is asking about: the sheet fades and only it stands out. */
  focusedTrayType?: string | null;
  areRouteLinesHidden?: boolean;
  /** He is carrying a corner or an edge: stop offering what a click cannot take. */
  reshapeView?: CableReshapeView | null;
  highlightMarker?: { x: number; y: number } | null;
  /** Where the pinned device's cable rises and drops. */
  heightMarks?: { x: number; y: number; kind: "rise" | "drop" }[];
  /** The zone he is drawing right now, corner by corner. */
  zoneDraft?: {
    points: { x: number; y: number }[];
    cursor: { x: number; y: number } | null;
  } | null;
  planWalls?: {
    /** `wall-12` or `door-5`. */
    id: string;
    kind: "wall" | "opening";
    /** A wall's outline ring, or a door's two jamb points. */
    points: { x: number; y: number }[];
  }[];
  /** While set, a click on a wall selects it; the selected one is drawn lit. */
  wallSelection?: {
    selectedWallId: string | null;
    onSelect: (wallId: string) => void;
  } | null;
  wallDraft?: {
    kind: "wall" | "opening";
    points: { x: number; y: number }[];
    cursor: { x: number; y: number } | null;
  } | null;
  /** Zuordnen: every selectable device as its symbol's box. */
  assignDeviceBoxes?: AssignDeviceBox[];
  /** Committed feed areas — a board's own region of the plan. */
  feedAreas?: {
    id: string;
    label: string;
    points: { x: number; y: number }[];
    /** The zone of the board Karl is asking about; the others step back. */
    isFocused?: boolean;
  }[];
  pickZoneCursor?: PickZoneCursor;
  routeCreationOverlay?: RouteCreationOverlayState | null;
  polygonsOverlay?: PolygonsOverlayState | null;
  floorPlanLayoutOverlay?: FloorPlanLayoutOverlay | null;
  imageScaleFactor?: number;
  rotation?: PageRotation;
  onRotate?: () => void;
  skipInitialLoadingState?: boolean;
  /** Draw grabbable node handles on this circuit's runs only. */
  nodeHandlesForRouteId?: string | null;
  /** Present only while the selected bbox may be moved or resized; draws its drag targets. */
  onSelectedBboxChange?: (bbox: BboxRect) => void;
  areNotesHidden?: boolean;
}

export const ProgressReportViewer = forwardRef<
  FloorPlanPanelHandlers,
  IProgressReportViewer
>(function ProgressReportViewer(
  {
    plan,
    detectedObjects,
    selectedObject,
    setSelectedObject,
    isAutoZoomEnabled = true,
    showObjectNavigation = true,
    navigationObjects,
    onAfterNavigate,
    zoomToFitOnImageLoad = "always",
    renderToolbar,
    mode = "default",
    exportActions,
    onMapClick,
    onMapMouseMove,
    onAreaSelect,
    onEmptyMapClick,
    backgroundObjects,
    floorPlanCablePaths,
    circuitPaths,
    nodeHandlesForRouteId,
    onSelectedBboxChange,
    circuitStrokeScale,
    circuitBoardMarkers,
    cableGhostMarker,
    removedMarkers,
    unroutedDeviceMarkers,
    candidateDeviceMarkers,
    selectedDeviceMarkers,
    selectedDeviceMarkersBlink,
    cableTrays,
    highlightTrayType,
    focusedTrayType,
    areRouteLinesHidden,
    reshapeView,
    highlightMarker,
    heightMarks,
    zoneDraft,
    feedAreas,
    planWalls,
    wallDraft,
    wallSelection,
    assignDeviceBoxes,
    pickZoneCursor = "crosshair",
    routeCreationOverlay,
    polygonsOverlay,
    floorPlanLayoutOverlay,
    areNotesHidden,
    imageScaleFactor,
    rotation = 0,
    onRotate,
    skipInitialLoadingState,
  }: IProgressReportViewer,
  ref,
) {
  const { t } = useTranslation();
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [planImageLoaded, setPlanImageLoaded] = useState(false);
  const [naturalSize, setNaturalSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const isFirstImageLoad = useRef(true);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isHoveringObject, setIsHoveringObject] = useState(false);
  const [isHoveringPlan, setIsHoveringPlan] = useState(false);
  const dragStartCoords = useRef({ x: 0, y: 0 });
  const isZoomedToObject = useRef(false);
  const minZoomLevel = useRef<number>(ZOOM_CONFIG.MIN);

  // Mobile touch states
  const [isTouching, setIsTouching] = useState(false);
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0 });
  const [initialPinchDistance, setInitialPinchDistance] = useState(0);
  const [initialZoom, setInitialZoom] = useState(1);
  const latestZoomToFitOnImageLoad = useLatest(zoomToFitOnImageLoad);
  const latestZoomLevel = useLatest(zoomLevel);
  const latestPanOffset = useLatest(panOffset);
  const touchStartCoords = useRef({ x: 0, y: 0 });

  const flashTimeoutRef = useRef<number | null>(null);

  const svgOverlayRef = useRef<HTMLDivElement>(null);
  const planImageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const areaSelect = useAreaSelectGesture({
    containerRef,
    panOffset,
    zoomLevel,
    onAreaSelect,
  });

  const flashRipple = useFlashRipple({
    svgOverlayRef,
  });

  const isPickZoneMode = mode === "pick_zone";

  const effectiveMaxZoom = imageScaleFactor
    ? ZOOM_CONFIG.MAX / imageScaleFactor
    : ZOOM_CONFIG.MAX;

  const effectiveManualMaxZoom = imageScaleFactor
    ? ZOOM_CONFIG.MANUAL_MAX / imageScaleFactor
    : ZOOM_CONFIG.MANUAL_MAX;

  const effectiveOpacityThreshold = imageScaleFactor
    ? ZOOM_OPACITY_THRESHOLD / imageScaleFactor
    : ZOOM_OPACITY_THRESHOLD;

  const getZoomState = useCallback(
    (): ZoomState => ({
      zoomLevel: latestZoomLevel.current,
      panOffset: latestPanOffset.current,
    }),
    [latestZoomLevel, latestPanOffset],
  );

  const applyZoomState = useCallback((zoomState: ZoomState) => {
    setZoomLevel(zoomState.zoomLevel);
    setPanOffset(zoomState.panOffset);
  }, []);

  const { animateZoomTo, cancelZoomAnimation, getSettledZoomState } =
    useAnimatedZoom({
      durationMs: ZOOM_CONFIG.ANIMATED_ZOOM_DURATION_MS,
      getZoomState,
      applyZoomState,
    });

  const moveToZoomState = useCallback(
    (targetZoomState: ZoomState) => {
      const shouldAnimate =
        targetZoomState.zoomLevel >= effectiveOpacityThreshold;

      if (!shouldAnimate) {
        cancelZoomAnimation();
        applyZoomState(targetZoomState);
        return;
      }

      animateZoomTo(targetZoomState);
    },
    [
      effectiveOpacityThreshold,
      cancelZoomAnimation,
      applyZoomState,
      animateZoomTo,
    ],
  );

  const circleSizeScale = useMemo(() => {
    if (!imageScaleFactor) return 1;
    const width = plan.imageWidthPx;
    const height = plan.imageHeightPx;
    if (!width || !height || width <= 0 || height <= 0) return 1;
    const baseScale = Math.min(width, height) / REFERENCE_IMAGE_SIZE;
    const scaleFactor = imageScaleFactor ?? 1;
    if (scaleFactor === 1) return Math.max(baseScale, MIN_CIRCLE_SIZE_SCALE);
    // Compensate for the non-linear getCircleSize function.
    // Image mode zoom = PDF zoom * scaleFactor, so getCircleSize returns
    // different values for the same visual area. Multiply by the ratio
    // to match PDF mode circle sizes at all zoom levels.
    const pdfEquivalentCircleSize = getCircleSize(zoomLevel * scaleFactor);
    const imageCircleSize = getCircleSize(zoomLevel);
    const circleRatio = pdfEquivalentCircleSize / imageCircleSize;
    const result = baseScale * circleRatio;
    // Scale the floor so that screen-rendered circle sizes match PDF mode exactly.
    // PDF screen = getCircleSize(pdfZoom) * floor * pdfZoom
    // Image screen = getCircleSize(imgZoom) * adjustedFloor * imgZoom
    // Since imgZoom = pdfZoom / scaleFactor, multiply floor by scaleFactor * circleRatio.
    const adjustedMinScale = MIN_CIRCLE_SIZE_SCALE * scaleFactor * circleRatio;
    return Math.max(result, adjustedMinScale);
  }, [plan.imageWidthPx, plan.imageHeightPx, zoomLevel, imageScaleFactor]);

  const zoomToObject: ZoomToObject = useCallback(
    (obj) => {
      if (!obj || !containerRef.current || !planImageRef.current) return;

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();

      const targetZoom = Math.min(ZOOM_OPACITY_THRESHOLD, effectiveMaxZoom);

      const containerCenterX = containerRect.width / 2;
      const containerCenterY = containerRect.height / 2;

      const newPanX = containerCenterX - obj.x * targetZoom;
      const newPanY = containerCenterY - obj.y * targetZoom;

      moveToZoomState({
        zoomLevel: targetZoom,
        panOffset: { x: newPanX, y: newPanY },
      });
    },
    [effectiveMaxZoom, moveToZoomState],
  );

  const zoomToBbox: ZoomToBbox = useCallback(
    (bbox) => {
      if (!containerRef.current || !planImageRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const { zoom, panX, panY } = computeZoomToBbox({
        bbox,
        containerWidth: containerRect.width,
        containerHeight: containerRect.height,
        minZoom: minZoomLevel.current,
        maxZoom: effectiveManualMaxZoom,
      });

      moveToZoomState({ zoomLevel: zoom, panOffset: { x: panX, y: panY } });
    },
    [effectiveManualMaxZoom, moveToZoomState],
  );

  const zoomToFit = useCallback(() => {
    const container = containerRef.current;
    const img = planImageRef?.current;
    if (container && img) {
      const containerRect = container.getBoundingClientRect();
      // A turned raster occupies the swapped box, so fit to that.
      const { width: imgNaturalWidth, height: imgNaturalHeight } =
        getRotatedSize({
          rotation,
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      if (imgNaturalWidth && imgNaturalHeight) {
        const scaleX = containerRect.width / imgNaturalWidth;
        const scaleY = containerRect.height / imgNaturalHeight;
        const fitZoom = Math.min(scaleX, scaleY, 1);

        // Calculate center offset
        const renderedWidth = imgNaturalWidth * fitZoom;
        const renderedHeight = imgNaturalHeight * fitZoom;
        const centerX = (containerRect.width - renderedWidth) / 2;
        const centerY = (containerRect.height - renderedHeight) / 2;

        minZoomLevel.current = fitZoom;
        moveToZoomState({
          zoomLevel: fitZoom,
          panOffset: { x: centerX, y: centerY },
        });
      }
    }
  }, [rotation, moveToZoomState]);

  const handleImageOnLoad = useCallback(() => {
    const img = planImageRef.current;
    if (img) {
      setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
    }
    const shouldZoom =
      latestZoomToFitOnImageLoad.current === "always" ||
      (latestZoomToFitOnImageLoad.current === "initial" &&
        isFirstImageLoad.current);

    if (shouldZoom) {
      zoomToFit();
    }
    isFirstImageLoad.current = false;
    setTimeout(() => {
      setPlanImageLoaded(true);
    }, 100);
  }, [latestZoomToFitOnImageLoad, zoomToFit]);

  const zoomToElements: ZoomToElements = useCallback(
    ({ elements, padding = OBJECT_BASE_SIZE }) => {
      if (
        !elements ||
        elements.length === 0 ||
        !containerRef.current ||
        !planImageRef.current
      ) {
        return;
      }

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();

      // Compute bounding box of all elements
      let minX = Infinity;
      let maxX = -Infinity;
      let minY = Infinity;
      let maxY = -Infinity;

      elements.forEach((el) => {
        const x = el.x;
        const y = el.y;
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
      });

      const boxWidth = maxX - minX;
      const boxHeight = maxY - minY;
      const boxCenterX = (minX + maxX) / 2;
      const boxCenterY = (minY + maxY) / 2;

      // Calculate zoom needed to fit the box with padding
      const zoomX =
        (containerRect.width - padding * 2) / (boxWidth + padding * 2);
      const zoomY =
        (containerRect.height - padding * 2) / (boxHeight + padding * 2);
      const targetZoom = Math.min(zoomX, zoomY);
      const clampedZoom = Math.max(
        ZOOM_CONFIG.MIN,
        Math.min(effectiveMaxZoom, targetZoom),
      );

      // Calculate pan to center the box
      const containerCenterX = containerRect.width / 2;
      const containerCenterY = containerRect.height / 2;
      const newPanX = containerCenterX - boxCenterX * clampedZoom;
      const newPanY = containerCenterY - boxCenterY * clampedZoom;

      moveToZoomState({
        zoomLevel: clampedZoom,
        panOffset: { x: newPanX, y: newPanY },
      });
    },
    [effectiveMaxZoom, moveToZoomState],
  );

  const flashCanvas = useCallback((duration: number = 1500) => {
    if (!svgOverlayRef.current || !planImageRef.current) return;

    if (flashTimeoutRef.current !== null) {
      clearTimeout(flashTimeoutRef.current);
      svgOverlayRef.current.classList.remove("animate-flash");
      planImageRef.current.classList.remove("opacity-50");
    }

    svgOverlayRef.current.classList.add("animate-flash");
    planImageRef.current.classList.add("opacity-50");

    flashTimeoutRef.current = window.setTimeout(() => {
      svgOverlayRef.current?.classList.remove("animate-flash");
      planImageRef.current?.classList.remove("opacity-50");
      flashTimeoutRef.current = null;
    }, duration);
  }, []);

  const handleAfterNavigate = useCallback(
    (object: NavigableObject) => {
      if (onAfterNavigate) {
        onAfterNavigate(object);
        return;
      }
      if (isDetectedObject(object)) {
        zoomToBbox(object.bbox);
      }
    },
    [onAfterNavigate, zoomToBbox],
  );

  const objectNavigation = useObjectNavigation<NavigableObject>({
    objects: navigationObjects ?? detectedObjects,
    selectedId: selectedObject?.id ?? null,
    onSelectId: setSelectedObject,
    onAfterSelect: handleAfterNavigate,
  });

  const handleObjectClick = useCallback(
    (event: {
      clientX: number;
      clientY: number;
      preventDefault: () => void;
      stopPropagation: () => void;
    }) => {
      if (flashTimeoutRef.current !== null) return;

      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();

      // 1) Mouse position inside the container (screen/local space)
      const localX = event.clientX - rect.left;
      const localY = event.clientY - rect.top;

      // 2) Convert to "real" floor-plan coordinates (world/canvas space)
      const planX = (localX - panOffset.x) / zoomLevel;
      const planY = (localY - panOffset.y) / zoomLevel;

      // Find clicked object using plan coords
      const clickedObject = detectedObjects.find(
        (obj) =>
          planX >= obj.bbox.x1 &&
          planX <= obj.bbox.x2 &&
          planY >= obj.bbox.y1 &&
          planY <= obj.bbox.y2,
      );

      if (isPickZoneMode) {
        onMapClick?.({ x: planX, y: planY });
        return;
      }

      if (clickedObject) {
        setSelectedObject(clickedObject.id);
        return;
      }

      if (circuitPaths) {
        const clickedRouteId = findClickedRoute({
          paths: circuitPaths.paths,
          planX,
          planY,
          zoomLevel,
          circleSizeScale,
        });
        if (clickedRouteId) {
          setSelectedObject(clickedRouteId);
          return;
        }
      }

      onEmptyMapClick?.({ x: planX, y: planY });
    },
    [
      panOffset.x,
      panOffset.y,
      zoomLevel,
      detectedObjects,
      setSelectedObject,
      isPickZoneMode,
      onMapClick,
      onEmptyMapClick,
      circleSizeScale,
      circuitPaths,
    ],
  );

  const handleZoom = useCallback(
    (
      directionOrEvent: string | WheelEvent,
      mousePosition: { x: number; y: number } | null = null,
    ) => {
      if (!containerRef.current) return;

      let delta;
      let zoomPoint = mousePosition;

      // Handle different input types
      if (typeof directionOrEvent === "string") {
        // Button click - simple direction string
        delta =
          directionOrEvent === "in"
            ? ZOOM_CONFIG.BUTTON_IN_FACTOR
            : ZOOM_CONFIG.BUTTON_OUT_FACTOR;
      } else if (directionOrEvent && directionOrEvent.deltaY !== undefined) {
        const event = directionOrEvent;

        delta = -normalizeWheelDelta({
          e: event,
          pixelMultiplier: ZOOM_CONFIG.WHEEL_PIXEL_MODE_MULTIPLIER,
          lineMultiplier: ZOOM_CONFIG.WHEEL_LINE_MODE_MULTIPLIER,
          maxDelta: ZOOM_CONFIG.WHEEL_MAX_DELTA,
        });

        if (!zoomPoint) {
          zoomPoint = {
            x: event.clientX,
            y: event.clientY,
          };
        }
      } else {
        console.warn("Invalid zoom input");
        return;
      }

      // Calculate exponential zoom factor
      const isButtonZoom = typeof directionOrEvent === "string";
      const factor = Math.exp(
        isButtonZoom ? delta : delta * ZOOM_CONFIG.WHEEL_SENSITIVITY,
      );
      // Repeated button clicks build on the running animation's target, not a mid-glide frame
      const { zoomLevel: baseZoomLevel, panOffset: basePanOffset } =
        getSettledZoomState();
      const newZoom = baseZoomLevel * factor;
      const clampedZoom = Math.max(
        minZoomLevel.current,
        Math.min(effectiveManualMaxZoom, newZoom),
      );

      // Calculate pan offset to maintain zoom point position
      const containerRect = containerRef.current.getBoundingClientRect();
      const mx = zoomPoint
        ? zoomPoint.x - containerRect.left
        : containerRect.width / 2;
      const my = zoomPoint
        ? zoomPoint.y - containerRect.top
        : containerRect.height / 2;

      const imageX = (mx - basePanOffset.x) / baseZoomLevel;
      const imageY = (my - basePanOffset.y) / baseZoomLevel;

      const nextZoomState: ZoomState = {
        zoomLevel: clampedZoom,
        panOffset: {
          x: mx - imageX * clampedZoom,
          y: my - imageY * clampedZoom,
        },
      };

      if (isButtonZoom) {
        moveToZoomState(nextZoomState);
        return;
      }

      applyZoomState(nextZoomState);
    },
    [
      effectiveManualMaxZoom,
      getSettledZoomState,
      moveToZoomState,
      applyZoomState,
    ],
  );

  const handleContainerMouseMove: ReactMouseEventHandler<HTMLDivElement> = (
    event,
  ) => {
    if (areaSelect.handleMouseMove(event)) return;
    if (!containerRef.current || isDragging || !planImageLoaded) {
      return;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    let moveX = event.clientX - containerRect.left;
    let moveY = event.clientY - containerRect.top;

    moveX = (moveX - panOffset.x) / zoomLevel;
    moveY = (moveY - panOffset.y) / zoomLevel;

    const isOverObject = detectedObjects.some(
      (obj) =>
        moveX >= obj.bbox.x1 &&
        moveX <= obj.bbox.x2 &&
        moveY >= obj.bbox.y1 &&
        moveY <= obj.bbox.y2,
    );

    if (isOverObject !== isHoveringObject) {
      setIsHoveringObject(isOverObject);
    }

    onMapMouseMove?.({ x: moveX, y: moveY });
  };

  const handleMouseDown: ReactMouseEventHandler<HTMLDivElement> = (event) => {
    if (areaSelect.handleMouseDown(event)) return;
    // Handle both mouse clicks and trackpad clicks; the middle button always pans
    if (event.button === 0 || event.button === 1) {
      // Left mouse button or trackpad click
      cancelZoomAnimation();
      setIsDragging(true);
      setDragStart({
        x: event.clientX - panOffset.x,
        y: event.clientY - panOffset.y,
      });
      dragStartCoords.current = { x: event.clientX, y: event.clientY };
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handleMouseMove = useCallback(
    (event: MouseEvent | ReactMouseEvent<HTMLDivElement>) => {
      if (isDragging) {
        cancelZoomAnimation();
        const newPanOffset = {
          x: event.clientX - dragStart.x,
          y: event.clientY - dragStart.y,
        };
        setPanOffset(newPanOffset);
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [isDragging, dragStart, cancelZoomAnimation],
  );

  const handleMouseUp = useCallback(
    (event: MouseEvent | ReactMouseEvent<HTMLDivElement>) => {
      if (areaSelect.handleMouseUp(event, () => handleObjectClick(event))) {
        return;
      }
      if (isDragging) {
        setIsDragging(false);

        const dx = Math.abs(event.clientX - dragStartCoords.current.x);
        const dy = Math.abs(event.clientY - dragStartCoords.current.y);

        if (dx < 5 && dy < 5) {
          handleObjectClick(event);
        }

        event.preventDefault();
        event.stopPropagation();
      }
    },
    [isDragging, handleObjectClick, areaSelect],
  );

  const handleMouseEnter = () => {
    setIsHoveringPlan(true);
  };

  const handleMouseLeave = () => {
    // Stop dragging if mouse leaves the container
    setIsDragging(false);
    areaSelect.cancel();
    setIsHoveringPlan(false);
    onMapMouseMove?.(null);
  };

  // Mobile touch event handlers
  const getTouchDistance = (
    touches: ReactTouchEvent<HTMLDivElement>["touches"],
  ) => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getTouchCenter = (
    touches: ReactTouchEvent<HTMLDivElement>["touches"],
  ) => {
    if (touches.length === 1) {
      return { x: touches[0].clientX, y: touches[0].clientY };
    }
    const x = (touches[0].clientX + touches[1].clientX) / 2;
    const y = (touches[0].clientY + touches[1].clientY) / 2;
    return { x, y };
  };

  const handleTouchStart: ReactTouchEventHandler<HTMLDivElement> = (event) => {
    event.preventDefault();
    cancelZoomAnimation();
    if (event.touches.length === 1) {
      // Single finger - start panning
      setIsTouching(true);
      const touch = event.touches[0];
      setTouchStart({
        x: touch.clientX - panOffset.x,
        y: touch.clientY - panOffset.y,
      });
      touchStartCoords.current = { x: touch.clientX, y: touch.clientY };
    } else if (event.touches.length === 2) {
      // Two fingers - start pinch zoom
      setIsTouching(false); // Stop panning
      const distance = getTouchDistance(event.touches);
      setInitialPinchDistance(distance);
      setInitialZoom(zoomLevel);
    }
  };

  const handleTouchMove = useCallback(
    (event: Event | ReactTouchEvent<HTMLDivElement>) => {
      event.preventDefault();
      cancelZoomAnimation();
      if ("touches" in event && event.touches.length === 1 && isTouching) {
        // Single finger - pan
        const touch = event.touches[0];
        const newPanOffset = {
          x: touch.clientX - touchStart.x,
          y: touch.clientY - touchStart.y,
        };
        setPanOffset(newPanOffset);
      } else if ("touches" in event && event.touches.length === 2) {
        // Two fingers - pinch zoom
        const distance = getTouchDistance(event.touches);
        const center = getTouchCenter(event.touches);

        if (initialPinchDistance > 0) {
          const scale = distance / initialPinchDistance;
          const newZoom = Math.max(
            0.1,
            Math.min(effectiveManualMaxZoom, initialZoom * scale),
          );

          // Calculate zoom with center point
          const containerRect = containerRef.current?.getBoundingClientRect();
          if (containerRect) {
            const mousePosition = {
              x: center.x - containerRect.left,
              y: center.y - containerRect.top,
            };

            // Apply zoom centered on pinch point
            const zoomFactor = newZoom / zoomLevel;
            const newPanX =
              mousePosition.x - (mousePosition.x - panOffset.x) * zoomFactor;
            const newPanY =
              mousePosition.y - (mousePosition.y - panOffset.y) * zoomFactor;

            setZoomLevel(newZoom);
            setPanOffset({ x: newPanX, y: newPanY });
          }
        }
      }
    },
    [
      isTouching,
      touchStart,
      initialPinchDistance,
      initialZoom,
      zoomLevel,
      panOffset,
      effectiveManualMaxZoom,
      cancelZoomAnimation,
    ],
  );

  const handleTouchEnd = useCallback(
    (event: Event | ReactTouchEvent<HTMLDivElement>) => {
      if ("touches" in event && event.touches.length === 0) {
        // All fingers lifted
        if (isTouching) {
          setIsTouching(false);

          // Check if it was a tap (minimal movement)
          const touch = event.changedTouches[0];
          const dx = Math.abs(touch.clientX - touchStartCoords.current.x);
          const dy = Math.abs(touch.clientY - touchStartCoords.current.y);

          if (dx < 10 && dy < 10) {
            // Simulate click for object selection
            const syntheticEvent = {
              clientX: touch.clientX,
              clientY: touch.clientY,
              preventDefault: () => {},
              stopPropagation: () => {},
            };
            handleObjectClick(syntheticEvent);
          }
        }

        // Reset pinch zoom state
        setInitialPinchDistance(0);
        setInitialZoom(1);
      } else if ("touches" in event && event.touches.length === 1) {
        // One finger remaining, restart single touch
        setInitialPinchDistance(0);
        if (!isTouching) {
          const touch = event.touches[0];
          setIsTouching(true);
          setTouchStart({
            x: touch.clientX - panOffset.x,
            y: touch.clientY - panOffset.y,
          });
          touchStartCoords.current = { x: touch.clientX, y: touch.clientY };
        }
      }
    },
    [isTouching, handleObjectClick, panOffset],
  );

  const handleWheel = useCallback(
    (event: WheelEvent) => {
      event.preventDefault();
      cancelZoomAnimation();
      handleZoom(event);
    },
    [cancelZoomAnimation, handleZoom],
  );

  useImperativeHandle(
    ref,
    () => ({
      zoomToFit,
      zoomToObject,
      zoomToBbox,
      zoomToElements,
      flashCanvas,
      flashRipple,
      // Mid-glide, the resolution switch must carry over the destination, not a halfway frame
      getZoomState: getSettledZoomState,
      setZoomState: (state) => {
        cancelZoomAnimation();
        applyZoomState(state);
      },
    }),
    [
      zoomToFit,
      zoomToObject,
      zoomToBbox,
      zoomToElements,
      flashCanvas,
      flashRipple,
      getSettledZoomState,
      cancelZoomAnimation,
      applyZoomState,
    ],
  );

  useEffect(() => {
    isZoomedToObject.current = false;
  }, [selectedObject?.id]);

  useEffect(() => {
    if (
      isAutoZoomEnabled &&
      selectedObject &&
      planImageLoaded &&
      !isZoomedToObject.current
    ) {
      const timeoutId = setTimeout(() => {
        zoomToBbox(selectedObject.bbox);
        isZoomedToObject.current = true;
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isAutoZoomEnabled, selectedObject, planImageLoaded, zoomToBbox]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("wheel", handleWheel, { passive: false });

      return () => {
        container.removeEventListener("wheel", handleWheel);
      };
    }
  }, [handleWheel]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, dragStart]);

  useEffect(() => {
    if (isTouching) {
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd, { passive: false });

      return () => {
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTouching, touchStart]);

  useKeyboardNavigation({
    goPrev: objectNavigation.goPrev,
    goNext: objectNavigation.goNext,
    isDisabled: !showObjectNavigation || isPickZoneMode,
  });

  return (
    <div className="h-full bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="relative h-full w-full flex">
        <div
          className="flex-1 relative bg-white rounded-lg border overflow-hidden w-full h-full"
          ref={containerRef}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onMouseMove={handleContainerMouseMove}
          style={{
            cursor:
              onAreaSelect && !areaSelect.isSpaceDown
                ? "crosshair"
                : isPickZoneMode && pickZoneCursor === "crosshair"
                  ? "crosshair"
                  : isHoveringObject
                    ? "pointer"
                    : isDragging || isTouching
                      ? "grabbing"
                      : "grab",
            touchAction: "none", // Prevent default touch behaviors
          }}
        >
          <AreaSelectBox box={areaSelect.selectionBox} />
          <div
            className="relative w-full h-full"
            style={{ overflow: "hidden" }}
          >
            <div
              className="relative w-full h-full"
              style={{
                transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
                transformOrigin: "top left",
                visibility: planImageLoaded ? "visible" : "hidden",
              }}
            >
              <PlanImage
                ref={planImageRef}
                src={plan.previewUrl}
                alt={t("progress_report.plan_alt", {
                  sectionName: plan.name,
                })}
                style={
                  naturalSize
                    ? getRasterRotationStyle({
                        rotation,
                        naturalWidth: naturalSize.width,
                        naturalHeight: naturalSize.height,
                      })
                    : undefined
                }
                onLoad={handleImageOnLoad}
              />
            </div>
          </div>
          {!planImageLoaded && !skipInitialLoadingState && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          )}
          {(planImageLoaded || skipInitialLoadingState) && (
            <div
              ref={svgOverlayRef}
              className="absolute inset-0 pointer-events-none"
            >
              <SvgOverlay
                nodeHandlesForRouteId={nodeHandlesForRouteId}
                onSelectedBboxChange={onSelectedBboxChange}
                onSelectNote={setSelectedObject}
                objects={detectedObjects}
                backgroundObjects={backgroundObjects}
                selectedObject={selectedObject}
                zoomLevel={zoomLevel}
                maxZoom={effectiveMaxZoom}
                panOffset={panOffset}
                floorPlanCablePaths={floorPlanCablePaths}
                circuitPaths={circuitPaths}
                circuitStrokeScale={circuitStrokeScale}
                circuitBoardMarkers={circuitBoardMarkers}
                cableGhostMarker={cableGhostMarker}
                removedMarkers={removedMarkers}
                unroutedDeviceMarkers={unroutedDeviceMarkers}
                candidateDeviceMarkers={candidateDeviceMarkers}
                selectedDeviceMarkers={selectedDeviceMarkers}
                selectedDeviceMarkersBlink={selectedDeviceMarkersBlink}
                cableTrays={cableTrays}
                highlightTrayType={highlightTrayType}
                focusedTrayType={focusedTrayType}
                areRouteLinesHidden={areRouteLinesHidden}
                reshapeView={reshapeView}
                highlightMarker={highlightMarker}
                heightMarks={heightMarks}
                zoneDraft={zoneDraft}
                feedAreas={feedAreas}
                planWalls={planWalls}
                wallDraft={wallDraft}
                wallSelection={wallSelection}
                assignDeviceBoxes={assignDeviceBoxes}
                routeCreationOverlay={routeCreationOverlay}
                polygonsOverlay={polygonsOverlay}
                floorPlanLayoutOverlay={floorPlanLayoutOverlay}
                areNotesHidden={areNotesHidden}
                circleSizeScale={circleSizeScale}
                opacityThreshold={effectiveOpacityThreshold}
              />
            </div>
          )}
        </div>
        {showObjectNavigation && (
          <ObjectNavigationBar
            canPrev={!isPickZoneMode && objectNavigation.canGoPrev}
            canNext={!isPickZoneMode && objectNavigation.canGoNext}
            onPrev={objectNavigation.goPrev}
            onNext={objectNavigation.goNext}
            onSelectIndex={objectNavigation.goToIndex}
            selectedIndex={objectNavigation.selectedIndex}
            total={objectNavigation.total}
          />
        )}
        <ZoomControls
          isDisabled={!planImageLoaded}
          onZoomIn={() => handleZoom("in")}
          onZoomOut={() => handleZoom("out")}
          onZoomToObject={
            selectedObject ? () => zoomToBbox(selectedObject.bbox) : undefined
          }
          onRotate={onRotate}
          onReset={zoomToFit}
        />
        <PlanNameChip
          name={plan.name}
          isArchived={plan.isArchived}
          exportActions={exportActions}
        />
        {renderToolbar?.({ isDisabled: !planImageLoaded, isHoveringPlan })}
      </div>
    </div>
  );
});
